import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import {
  AccountBalanceWallet,
  Add,
  History,
  AttachMoney,
  Euro,
  CurrencyExchange,
  Refresh,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  getMyAccount,
  getAccountBalance,
  getMyTransactionHistory,
} from "api/wallet";
import {
  TrendingUp,
  TrendingDown,
  SwapHoriz,
  Payment,
  Undo,
} from "@mui/icons-material";
import notificationService from "services/notificationService";
import { ClientGetLatestExchangeRates } from "api/client";
import { formatWalletTxnDetail } from "utils/formatWalletTxnDetail";
import CompactExchangeRateDisplay from "components/CompactExchangeRateDisplay";
import ExchangeRateDisplay from "components/ExchangeRateDisplay";
import WalletActivationPanel from "./WalletActivationPanel";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

const WalletDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [balance, setBalance] = useState(null);
  const [walletActive, setWalletActive] = useState(true);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [exchangeRateLoading, setExchangeRateLoading] = useState(true);
  const [exchangeRateError, setExchangeRateError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadWalletData();
    loadExchangeRates();
    const interval = setInterval(loadWalletData, 60000);

    // Real-time: update balance silently when wallet is updated
    const unsubscribe = notificationService.subscribe(
      "wallet-update",
      (notification) => {
        if (
          notification?.subType === "approved" &&
          notification?.amount &&
          notification?.currency
        ) {
          // Update balance directly from notification payload — no loading flicker
          setBalance((prev) =>
            prev
              ? {
                  ...prev,
                  [notification.currency]:
                    (prev[notification.currency] || 0) + notification.amount,
                }
              : prev,
          );
          // Also refresh topup request status list silently
          loadWalletData(true);
        } else {
          loadWalletData(true);
        }
      },
    );

    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, []);

  const loadWalletData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError("");

      const [accountData, balanceData, txnData] = await Promise.all([
        getMyAccount(),
        getAccountBalance(),
        getMyTransactionHistory(1, 10, {}, ""),
      ]);

      setWalletActive(!!accountData.walletActive);
      setBalance(balanceData.data);
      if (txnData.status === 200) {
        setRecentTransactions(txnData.data);
        setTotalTransactions(txnData.total || 0);
      }
    } catch (err) {
      setError(err.message || "Failed to load wallet data");
      console.error("Error loading wallet data:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadExchangeRates = async () => {
    try {
      setExchangeRateLoading(true);
      setExchangeRateError(null);

      const response = await ClientGetLatestExchangeRates();

      if (response && !response.error) {
        setExchangeRate(response);
      } else {
        setExchangeRateError(
          response?.message || "Failed to fetch exchange rates",
        );
      }
    } catch (err) {
      console.error("Error fetching exchange rates:", err);
      setExchangeRateError("Unable to connect to server");
    } finally {
      setExchangeRateLoading(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    const symbols = {
      USD: "$",
      THB: "฿",
      LAK: "₭",
    };
    return `${symbols[currency] || ""}${amount.toLocaleString()}`;
  };

  const getCurrencyFlag = (currency) => {
    const countryMapping = {
      USD: "us", // United States
      THB: "th", // Thailand
      LAK: "la", // Laos
    };

    const countryCode = countryMapping[currency] || "un"; // UN flag as default

    return (
      <img
        src={`https://flagcdn.com/16x12/${countryCode}.png`}
        alt={`${currency} flag`}
        style={{
          width: "16px",
          height: "12px",
          marginLeft: "4px",
          verticalAlign: "middle",
          borderRadius: "1px",
        }}
      />
    );
  };

  const purposeConfig = {
    deposit: {
      label: "Deposit",
      color: "success",
      icon: <TrendingUp fontSize="small" />,
      sign: "+",
    },
    refund: {
      label: "Refund",
      color: "success",
      icon: <Undo fontSize="small" />,
      sign: "+",
    },
    withdraw: {
      label: "Withdraw",
      color: "error",
      icon: <TrendingDown fontSize="small" />,
      sign: "-",
    },
    payment: {
      label: "Payment",
      color: "error",
      icon: <Payment fontSize="small" />,
      sign: "-",
    },
    transfer: {
      label: "Transfer",
      color: "info",
      icon: <SwapHoriz fontSize="small" />,
      sign: "",
    },
    exchange: {
      label: "Exchange",
      color: "info",
      icon: <SwapHoriz fontSize="small" />,
      sign: "",
    },
    reversal: {
      label: "Reversal",
      color: "warning",
      icon: <Undo fontSize="small" />,
      sign: "",
    },
    adjustment: {
      label: "Adjustment",
      color: "warning",
      icon: <AccountBalanceWallet fontSize="small" />,
      sign: "",
    },
  };

  const getPurposeConfig = (txn) => {
    const purpose =
      txn.purpose || (txn.txn_type === "credit" ? "deposit" : "withdraw");
    return (
      purposeConfig[purpose] || {
        label: purpose,
        color: "default",
        icon: null,
        sign: "",
      }
    );
  };

  const getAmountSign = (txn) => {
    const cfg = getPurposeConfig(txn);
    if (cfg.sign) return cfg.sign;
    return txn.txn_type === "credit" ? "+" : "-";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading wallet...
        </Typography>
      </Box>
    );
  }

  if (!walletActive) {
    // Explains why the wallet is off and, when allowed, lets the customer
    // activate it themselves (terms + legal name + WhatsApp code).
    let customerCode = "";
    try { customerCode = JSON.parse(localStorage.getItem("customer") || "{}")?.customerCode || ""; } catch { /* ignore */ }
    return (
      <Box m={2}>
        <WalletActivationPanel customerCode={customerCode} onActivated={() => loadWalletData()} />
      </Box>
    );
  }

  return (
    <Box m={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4" sx={{ display: "flex", alignItems: "center" }}>
          <AccountBalanceWallet sx={{ mr: 1 }} />
          My Wallet
        </Typography>
        <Button
          color="secondary"
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={loadWalletData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Account Balance Cards - Modern Minimal Design */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "#ede7f6",
              border: "1px solid #d1c4e9",
              borderRadius: 3,
              boxShadow: "0 2px 12px rgba(103,58,183,0.08)",
              transition: "all 0.25s ease",
              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 24px rgba(103,58,183,0.15)" },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <AttachMoney sx={{ fontSize: 20, mr: 1, color: "#7e57c2", bgcolor: "#d1c4e9", borderRadius: "50%", p: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#5e35b1", textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.75rem" }}>
                    THB Balance
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: "1.8rem", md: "2.2rem" }, lineHeight: 1.2, color: "#4527a0" }}>
                    {balance ? formatCurrency(balance.balances.THB, "THB") : "฿0"}
                  </Typography>
                  {getCurrencyFlag("THB")}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "#fce4ec",
              border: "1px solid #f8bbd0",
              borderRadius: 3,
              boxShadow: "0 2px 12px rgba(233,30,99,0.08)",
              transition: "all 0.25s ease",
              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 24px rgba(233,30,99,0.15)" },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <Euro sx={{ fontSize: 20, mr: 1, color: "#e91e63", bgcolor: "#f8bbd0", borderRadius: "50%", p: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#c2185b", textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.75rem" }}>
                    LAK Balance
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: "1.8rem", md: "2.2rem" }, lineHeight: 1.2, color: "#880e4f" }}>
                    {balance ? formatCurrency(balance.balances.LAK, "LAK") : "₭0"}
                  </Typography>
                  {getCurrencyFlag("LAK")}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "#e3f2fd",
              border: "1px solid #bbdefb",
              borderRadius: 3,
              boxShadow: "0 2px 12px rgba(33,150,243,0.08)",
              transition: "all 0.25s ease",
              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 24px rgba(33,150,243,0.15)" },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <CurrencyExchange sx={{ fontSize: 20, mr: 1, color: "#1976d2", bgcolor: "#bbdefb", borderRadius: "50%", p: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#1565c0", textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.75rem" }}>
                    USD Balance
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: "1.8rem", md: "2.2rem" }, lineHeight: 1.2, color: "#0d47a1" }}>
                    {balance ? formatCurrency(balance.balances.USD, "USD") : "$0"}
                  </Typography>
                  {getCurrencyFlag("USD")}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: "#e8f5e9",
              border: "1px solid #c8e6c9",
              borderRadius: 3,
              boxShadow: "0 2px 12px rgba(76,175,80,0.08)",
              transition: "all 0.25s ease",
              "&:hover": { transform: "translateY(-3px)", boxShadow: "0 8px 24px rgba(76,175,80,0.15)" },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <AccountBalanceWallet sx={{ fontSize: 20, mr: 1, color: "#388e3c", bgcolor: "#c8e6c9", borderRadius: "50%", p: 0.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#2e7d32", textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.75rem" }}>
                    Total (THB)
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: "1.8rem", md: "2.2rem" }, lineHeight: 1.2, color: "#1b5e20" }}>
                    {balance ? formatCurrency(balance.totalInTHB, "THB") : "฿0"}
                  </Typography>
                  {getCurrencyFlag("THB")}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Exchange Rate Display Banner */}
      <CompactExchangeRateDisplay
        exchangeRate={exchangeRate}
        loading={exchangeRateLoading}
        error={exchangeRateError}
        onClick={() => setDialogOpen(true)}
      />

      {/* Action Buttons */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            size="large"
            startIcon={<Add />}
            onClick={() => navigate("/customer-portal/wallet/topup")}
            sx={{ py: 2 }}
          >
            Top Up Wallet
          </Button>
        </Grid>
        <Grid item xs={6} md={3}>
          <Button
            color="secondary"
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<History />}
            onClick={() => navigate("/customer-portal/wallet/requests")}
            sx={{ py: 2 }}
          >
            Top-up Requests
          </Button>
        </Grid>
        <Grid item xs={6} md={3}>
          <Button
            color="secondary"
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<History />}
            onClick={() => navigate("/customer-portal/wallet/history")}
            sx={{ py: 2 }}
          >
            Transactions
          </Button>
        </Grid>
        <Grid item xs={6} md={3}>
          <Button
            color="secondary"
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<CurrencyExchange />}
            onClick={() => navigate("/customer-portal/wallet/exchange")}
            sx={{ py: 2 }}
          >
            Exchange Currency
          </Button>
        </Grid>
        <Grid item xs={6} md={3}>
          <Button
            color="secondary"
            variant="outlined"
            fullWidth
            size="large"
            startIcon={<AccountBalanceWallet />}
            onClick={() => navigate("/customer-portal/wallet/withdrawal")}
            sx={{ py: 2 }}
          >
            Withdraw
          </Button>
        </Grid>
      </Grid>

      {/* Recent Transactions */}
      <Paper sx={{ p: 2 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, display: "flex", alignItems: "center" }}
        >
          <History sx={{ mr: 1 }} />
          Recent Transactions
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {recentTransactions.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body1" color="text.secondary">
              No transactions found. Top up your wallet to get started.
            </Typography>
          </Box>
        ) : (
          <>
            {isMobile ? (
              <Grid container spacing={2}>
                {recentTransactions.map((txn) => {
                  const cfg = getPurposeConfig(txn);
                  const sign = getAmountSign(txn);
                  return (
                    <Grid item xs={12} key={txn._id}>
                      <Card
                        sx={{
                          borderRadius: 2,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        }}
                      >
                        <CardContent sx={{ p: 2 }}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="flex-start"
                          >
                            <Box>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                                mb={0.5}
                              >
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="bold"
                                  color={
                                    txn.txn_type === "credit"
                                      ? "success.main"
                                      : txn.txn_type === "debit"
                                        ? "error.dark"
                                        : "text.primary"
                                  }
                                >
                                  {sign}
                                  {formatCurrency(
                                    txn.amount || 0,
                                    txn.currency,
                                  )}
                                </Typography>
                                {getCurrencyFlag(txn.currency)}
                              </Box>
                              <Chip
                                icon={cfg.icon}
                                label={cfg.label}
                                color={cfg.color}
                                size="small"
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {txn.createdAt ? formatDate(txn.createdAt) : "—"}
                            </Typography>
                          </Box>
                          {formatWalletTxnDetail(txn) && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 1 }}
                              noWrap
                            >
                              {formatWalletTxnDetail(txn)}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Balance After</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentTransactions.map((txn) => {
                      const cfg = getPurposeConfig(txn);
                      const sign = getAmountSign(txn);
                      return (
                        <TableRow key={txn._id} hover>
                          <TableCell>
                            <Chip
                              icon={cfg.icon}
                              label={cfg.label}
                              color={cfg.color}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                              gap={0.5}
                            >
                              <Typography
                                variant="body2"
                                fontWeight="bold"
                                color={
                                  txn.txn_type === "credit"
                                    ? "success.main"
                                    : txn.txn_type === "debit"
                                      ? "error.dark"
                                      : "text.primary"
                                }
                              >
                                {sign}
                                {formatCurrency(txn.amount || 0, txn.currency)}
                              </Typography>
                              {getCurrencyFlag(txn.currency)}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="flex-end"
                              gap={0.5}
                            >
                              <Typography variant="body2">
                                {formatCurrency(txn.balanceAfter, txn.currency)}
                              </Typography>
                              {getCurrencyFlag(txn.currency)}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {txn.createdAt ? formatDate(txn.createdAt) : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              title={formatWalletTxnDetail(txn)}
                            >
                              {formatWalletTxnDetail(txn) || "—"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {recentTransactions.length > 0 && (
          <Box mt={2} textAlign="center">
            <Button
              color="secondary"
              variant="text"
              onClick={() => navigate("/customer-portal/wallet/history")}
            >
              View All Transactions ({totalTransactions})
            </Button>
          </Box>
        )}
      </Paper>

      {/* Exchange Rate Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Exchange Rate Details</Typography>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setDialogOpen(false)}
            sx={{ minWidth: "auto", p: 1 }}
          >
            ×
          </Button>
        </DialogTitle>
        <DialogContent>
          <ExchangeRateDisplay
            exchangeRate={exchangeRate}
            loading={exchangeRateLoading}
            error={exchangeRateError}
          />

          {/* Understanding Exchange Rates Section */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <CurrencyExchange
                  sx={{ mr: 1, color: theme.palette.secondary.main }}
                />
                <Typography variant="h6">
                  Understanding Exchange Rates
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    How USD Conversion Works:
                  </Typography>
                  <Typography variant="body2" paragraph>
                    When you have USD balance in your wallet, it's converted to
                    THB using the current exchange rate. For example, if the
                    rate is 34.50, then $1 USD equals 34.50 THB.
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    How LAK Conversion Works:
                  </Typography>
                  <Typography variant="body2" paragraph>
                    LAK (Lao Kip) is converted to THB by dividing your LAK
                    balance by the exchange rate. For example, if the rate is
                    600, then 600 LAK equals 1 THB.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Rate Updates:
                  </Typography>
                  <Typography variant="body2">
                    Exchange rates are updated regularly by our administrators
                    to reflect current market conditions. The rates shown above
                    are the most recent active rates in our system.
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Refresh Info */}
          <Box mt={2} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              Exchange rates are automatically refreshed. Last updated:{" "}
              {new Date().toLocaleString()}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            variant="outlined"
            color="secondary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WalletDashboard;
